import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import useAppContext from 'flow/hooks/useAppContext';
import useExtension from 'flow/hooks/useExtension';
import { selectNumberOfItemsForBadgeInAgentView, selectShouldSeeTicketsTab } from 'flow/selectors/TicketsSelectors';
import { routes } from 'flow/types/routes';
import { calcTotalUnreadMessages } from 'flow/utils';
import { calcTotalUnreadVoiceMessages } from 'flow/utils/extension-utils';
import { calcTotalUnreadChannelMessages, calcTotalUnreadVoiceMessagesForVoicemails } from 'flow/utils/pbx-utils';
import { generatePath } from 'flow/utils/router-utils';
import { twMerge } from 'tailwind-merge';
import React from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { NavLink } from 'react-router-dom';
import Icon from './Icon';
import Notification from './Notification';
import Tooltip from './Tooltip';
import { selectOwnExtensionKey } from 'flow/selectors/ExtensionSelectors';
import { loggedInUserHasOmnichannelFeature } from 'flow/selectors/extension-selectors';
import { selectNumberOfCentionErrandsRelevantForUser } from 'flow/selectors/CentionOmniSelectors';
import useHasAiStudioAccess from 'flow/pages/ai-studio/hooks/useHasAiStudioAccess';
import { isSlimmedTeamsApp } from 'flow/utils/app-utils';

/**
 * Marks a main navigation tab in the DOM, so behaviour outside this component
 * can recognise one — see dropFocusNotAskedFor in
 * route-components/ContextComponent.
 *
 * Deliberately not the `data-test-id`, which should stay free to change with
 * tests.
 */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export var NAV_TAB_ATTRIBUTE = 'data-nav-tab';
var ticketsTab = {
  iconKey: 'headset',
  key: 'tickets',
  tooltip: 'route.tickets',
  route: routes['tickets']
};
var aiStudioTab = {
  iconKey: 'autoAwesome',
  key: 'ai-studio',
  tooltip: 'route.ai-studio',
  route: routes['ai-studio.page']
};
var omniTab = {
  iconKey: 'support_agent',
  key: 'omni',
  tooltip: 'route.omni',
  route: routes['omni']
};
var messagesTab = {
  iconKey: 'chat',
  key: 'messages',
  tooltip: 'route.messages',
  route: routes['messages']
};
var pbxTab = {
  iconKey: 'ivr',
  key: 'pbx',
  tooltip: 'route.pbx',
  route: routes['pbx']
};
var AppNavigation = function AppNavigation() {
  var _useAppContext = useAppContext(),
    embedded = _useAppContext.embedded,
    i18next = _useAppContext.i18next;
  var _useExtension = useExtension(),
    ownExtension = _useExtension.ownExtension;
  var ownExtensionKey = useAppSelector(selectOwnExtensionKey);
  var _useHasAiStudioAccess = useHasAiStudioAccess(),
    hasUserAccessAiStudio = _useHasAiStudioAccess.hasUserAccessAiStudio;
  var sessions = useAppSelector(function (state) {
    return state.chats.get('sessions');
  });
  var channelsMap = useAppSelector(function (state) {
    return state.channels.get('channels');
  });
  var chatUserKey = useAppSelector(function (state) {
    return state.app.getIn(['extension', 'chatUserKey']);
  });
  var missedCallCount = useAppSelector(function (state) {
    return state.app.get('missedCallCount');
  });
  var sharedVoicemails = useAppSelector(function (state) {
    return state.pbx.get('voicemails');
  });
  var shouldSeeTicketsTab = useAppSelector(selectShouldSeeTicketsTab);
  var ticketsAssignedToUserCount = useAppSelector(function (state) {
    return selectNumberOfItemsForBadgeInAgentView(state) || 0;
  });
  var showOmnichannelItem = useAppSelector(loggedInUserHasOmnichannelFeature);
  var totalNumberOfOmnichannelErrands = useAppSelector(function (state) {
    return showOmnichannelItem ? selectNumberOfCentionErrandsRelevantForUser(state) : 0;
  });
  var badges = {
    messages: calcTotalUnreadMessages(sessions),
    pbx: calcTotalUnreadChannelMessages(channelsMap, chatUserKey) + calcTotalUnreadVoiceMessagesForVoicemails(sharedVoicemails),
    calls: calcTotalUnreadVoiceMessages(ownExtension.get('voiceMessages')) + missedCallCount,
    tickets: ticketsAssignedToUserCount,
    omni: totalNumberOfOmnichannelErrands ? totalNumberOfOmnichannelErrands : 0
  };
  var slimmed = isSlimmedTeamsApp(embedded);
  var tabs = [{
    iconKey: 'accountBox',
    key: 'extensions',
    tooltip: 'route.extensions',
    route: routes['extensions']
  }].concat(_toConsumableArray(slimmed ? [] : [messagesTab, pbxTab]), [{
    iconKey: 'callHistory',
    key: 'calls',
    tooltip: 'route.calls',
    route: routes['calls']
  }], _toConsumableArray(shouldSeeTicketsTab ? [ticketsTab] : []), _toConsumableArray(showOmnichannelItem ? [omniTab] : []), _toConsumableArray(hasUserAccessAiStudio ? [aiStudioTab] : []), [{
    iconKey: 'general',
    key: 'settings',
    tooltip: 'route.settings',
    route: generatePath(routes['settings'], {
      extensionKey: ownExtensionKey
    })
  }]);
  return /*#__PURE__*/_jsx("nav", {
    className: "flex items-center rounded-sm bg-gray-200",
    children: tabs.map(function (tab) {
      var label = i18next.t(tab.tooltip);
      return /*#__PURE__*/_jsx(Tooltip, {
        label: label,
        children: /*#__PURE__*/_jsxs(NavLink, _objectSpread(_objectSpread({
          "data-test-id": "tab-".concat(tab.key)
        }, _defineProperty({}, NAV_TAB_ATTRIBUTE, '')), {}, {
          to: tab.route,
          className: twMerge('hover:darken-10 focus-ring relative flex items-center bg-gray-200 px-4 py-2 text-gray-500 first:rounded-s-sm last:rounded-e-sm hover:rounded-sm hover:text-gray-600 focus-visible:z-10'),
          activeClassName: "bg-main text-iconOnBrand rounded-sm hover:text-white dark:hover:!text-gray-800",
          children: [/*#__PURE__*/_jsx(Icon, {
            iconKey: tab.iconKey,
            className: ['messages'].includes(tab.key) ? 'rtl:scale-x-[-1]' : ''
          }), /*#__PURE__*/_jsx("span", {
            className: "sr-only",
            children: label
          }), badges[tab.key] ? /*#__PURE__*/_jsx(Notification, {
            className: "me-1",
            children: badges[tab.key] < 100 ? badges[tab.key] : '99+'
          }) : null]
        }))
      }, tab.key);
    })
  });
};
export default AppNavigation;